import { FunctionComponent } from "react";
import { Event } from "@dto/Event";
import dayjs from "dayjs";

import "./EventDateTime.css";

const getTimeComponentOfDate = (date: dayjs.Dayjs) => {
  return date.hour() * 360 + date.minute() * 60 + date.second();
};

const getCompactFormattedDate = (
  startsOn: dayjs.Dayjs,
  endsOn: dayjs.Dayjs,
) => {
  if (startsOn.isSame(endsOn, "day")) {
    return {
      date: startsOn.format("MMM D"),
    };
  }

  return {
    date: startsOn.isSame(endsOn, "month")
      ? `${startsOn.format("MMM D")} – ${endsOn.format("D")}`
      : `${startsOn.format("MMM D")} – ${endsOn.format("MMM D")}`,
  };
};

const getFullFormattedDate = (
  startsOn: dayjs.Dayjs,
  endsOn: dayjs.Dayjs,
  startsOnTime: number,
  endsOnTime: number,
) => {
  const time = `${startsOn.format("h:mm a")} - ${endsOn.format("h:mm a")}`;

  if (startsOn.isSame(endsOn, "day")) {
    return {
      date: startsOn.format("ll"),
      time: startsOnTime !== endsOnTime ? time : startsOn.format("h:mm a"),
    };
  }

  return {
    date: startsOn.isSame(endsOn, "year")
      ? `${startsOn.format("MMM D")} - ${endsOn.format("MMM D, YYYY")}`
      : `${startsOn.format("ll")} - ${endsOn.format("ll")}`,
    time: startsOn.format("h:mm a") + " - " + endsOn.format("h:mm a"),
  };
};

const getFormattedDate = (
  event: Event,
  variant: "compact" | "full",
): { date: string; time?: string } | null => {
  if (!event.startsOn) {
    return null;
  }

  const startsOn = dayjs(event.startsOn).tz(VisitWidget.settings.timeZone);
  const endsOn = dayjs(event.endsOn ?? event.startsOn).tz(
    VisitWidget.settings.timeZone,
  );

  const startsOnTime = getTimeComponentOfDate(startsOn);
  const endsOnTime = getTimeComponentOfDate(endsOn);

  if (variant === "compact") {
    return getCompactFormattedDate(startsOn, endsOn);
  } else {
    return getFullFormattedDate(startsOn, endsOn, startsOnTime, endsOnTime);
  }
};

interface EventDateTimeProps {
  event: Event;
  /**
   * The style of the date formatting. `compact` is for list view while `full` is for entity view.
   */
  variant?: "compact" | "full";
}

const EventDateTime: FunctionComponent<EventDateTimeProps> = ({
  event,
  variant = "full",
}) => {
  const formattedDate = getFormattedDate(event, variant);

  if (!formattedDate) {
    return null;
  }

  return (
    <div
      className={`event-date-time-container${
        variant === "compact" ? " compact" : " full"
      }`}
      data-variant={variant}
      data-starts-on={event.startsOn}
      data-ends-on={event.endsOn}>
      <div className="event-date">{formattedDate.date}</div>
      {formattedDate.time && variant === "full" && (
        <div className="event-time">{formattedDate.time}</div>
      )}
    </div>
  );
};

export { EventDateTime };
